import { getUser } from "../auth/index.js";
import {
  buildWinterDoppelPersonOccupancy,
  catalogWinterDoppelOccupancyPeople,
  loadWinterDoppelOccupancyDeskExtras,
  loadWinterDoppelOccupancySeriesContexts,
  loadWinterDoppelSeries,
  matchOccupancyPersonForViewer,
  occupancyViewerFromAuth,
  pickUpcomingOccupancyItems,
} from "../winterdoppel/index.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "./hub-scoped-cache.js";

const HUB_SCHEDULE_CACHE_MS = 20000;
/** @type {Map<string, Promise<object[]>>} */
const __scheduleInflight = new Map();
/** @type {Map<string, { ts: number, value: object[] }>} */
const __scheduleCache = new Map();

export function clearHubPersonalScheduleCache() {
  __scheduleCache.clear();
  __scheduleInflight.clear();
}

registerHubCacheClear(clearHubPersonalScheduleCache);

export function localYmd(now = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export function localDateTimeMs(date, time) {
  const ymd = String(date || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return NaN;
  const [y, mo, d] = ymd.split("-").map(Number);
  const hm = String(time || "").slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(hm)) {
    const [h, mi] = hm.split(":").map(Number);
    return new Date(y, mo - 1, d, h, mi, 0, 0).getTime();
  }
  return new Date(y, mo - 1, d, 0, 0, 0, 0).getTime();
}

export function occupancyItemStartMs(item) {
  return localDateTimeMs(item?.date, item?.startTime);
}

export function occupancyItemEndMs(item) {
  const date = String(item?.date || "").slice(0, 10);
  const endTime = String(item?.endTime || "").slice(0, 5);
  if (/^\d{4}-\d{2}-\d{2}$/.test(date) && /^\d{2}:\d{2}$/.test(endTime)) {
    return localDateTimeMs(date, endTime);
  }
  const start = occupancyItemStartMs(item);
  const mins = Number(item?.durationMin);
  if (!Number.isFinite(start)) return NaN;
  if (!Number.isFinite(mins) || mins <= 0) return NaN;
  return start + mins * 60 * 1000;
}

export function isCancelledScheduleItem(item) {
  const status = String(
    item?.status || item?.sessionStatus || "",
  ).toLowerCase();
  return (
    status === "cancelled" || status === "canceled" || item?.cancelled === true
  );
}

export function classifyScheduleItem(item, now = new Date()) {
  if (isCancelledScheduleItem(item)) return "cancelled";
  const start = occupancyItemStartMs(item);
  const end = occupancyItemEndMs(item);
  const ts = now.getTime();
  if (!Number.isFinite(start)) return "upcoming";
  if (ts < start) return "upcoming";
  if (Number.isFinite(end) && ts < end) return "live";
  return "completed";
}

export function mergeOccupancyItems(items) {
  const byKey = new Map();
  for (const item of items || []) {
    const key = [
      item.source || "",
      item.seriesId || "",
      item.sessionId || "",
      item.date || "",
      item.startTime || "",
      item.seriesName || "",
    ].join("|");
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { ...item });
      continue;
    }
    if (item.role === "trainer") prev.role = "trainer";
    if (item.role === "reserve") prev.role = "reserve";
  }
  return [...byKey.values()];
}

function rankStatus(status) {
  if (status === "live") return 0;
  if (status === "upcoming") return 1;
  return 2;
}

export function buildHubScheduleView(items, now = new Date()) {
  const today = localYmd(now);
  const classified = mergeOccupancyItems(items)
    .filter((item) => classifyScheduleItem(item, now) !== "cancelled")
    .map((item) => ({
      ...item,
      scheduleStatus: classifyScheduleItem(item, now),
    }));
  classified.sort((a, b) => {
    const rank = rankStatus(a.scheduleStatus) - rankStatus(b.scheduleStatus);
    if (rank) return rank;
    return occupancyItemStartMs(a) - occupancyItemStartMs(b);
  });
  const display = classified;
  const todayActive = classified.filter(
    (item) =>
      item.date === today &&
      (item.scheduleStatus === "live" || item.scheduleStatus === "upcoming"),
  );
  return {
    featured: display[0] || null,
    rest: display.slice(1),
    todayCount: todayActive.length,
    items: classified,
  };
}

export async function loadHubPersonalScheduleItems() {
  const fromDate = localYmd();
  const scope = await resolveHubReadScope();
  const cacheKey = `${scope.key}|${fromDate}`;
  const now = Date.now();
  const cached = __scheduleCache.get(cacheKey);
  if (cached && now - cached.ts < HUB_SCHEDULE_CACHE_MS) {
    return cached.value;
  }
  const inflight = __scheduleInflight.get(cacheKey);
  if (inflight) return inflight;

  const started = { ...scope };
  const promise = __loadHubPersonalScheduleItemsImpl(fromDate)
    .then((value) => {
      if (isHubReadScopeCurrent(started)) {
        __scheduleCache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__scheduleInflight.get(cacheKey) === promise) {
        __scheduleInflight.delete(cacheKey);
      }
    });

  __scheduleInflight.set(cacheKey, promise);
  return promise;
}

async function __loadHubPersonalScheduleItemsImpl(fromDate) {
  try {
    const [series, deskExtras] = await Promise.all([
      loadWinterDoppelSeries(),
      loadWinterDoppelOccupancyDeskExtras({ fromDate }),
    ]);
    const [contexts, user] = await Promise.all([
      // Kein toDate: Serien, die erst später starten, müssen in „Nächste Termine“ bleiben.
      loadWinterDoppelOccupancySeriesContexts(series || [], { fromDate }),
      getUser(),
    ]);
    const people = catalogWinterDoppelOccupancyPeople(contexts, {
      trainers: deskExtras.trainers,
    });
    const person = matchOccupancyPersonForViewer(
      people,
      occupancyViewerFromAuth(user, deskExtras.trainers),
    );
    const occupancy = person?.key
      ? buildWinterDoppelPersonOccupancy({
          seriesContexts: contexts,
          selectedKeys: [person.key],
          people,
          trainers: deskExtras.trainers,
          deskSessions: deskExtras.deskSessions,
          deskLinkIds: deskExtras.deskLinkIds,
          clubPeopleById: deskExtras.clubPeopleById,
          fromDate,
        })
      : { days: [] };
    return mergeOccupancyItems(
      pickUpcomingOccupancyItems(occupancy.days, { limit: 48 }),
    );
  } catch {
    return [];
  }
}

export async function loadHubPersonalSchedule(now = new Date()) {
  const raw = await loadHubPersonalScheduleItems();
  return { ...buildHubScheduleView(raw, now), raw };
}
